import React from "react";
import {Box, Typography, Grid} from "@material-ui/core";
import styled from "styled-components";
import {FaCheck} from "react-icons/fa";

const Row = styled.div`
    padding-block: 14px;
`;

const Tag = styled.button`
    --bg: #f8fafc;
    --border: #e5e7eb;
    --text: #0f172a;
    --bg-active: #eef2ff;
    --border-active: #c7d2fe;

    appearance: none;
    width: 100%;
    border: 1px solid var(--border);
    background: var(--bg);
    color: var(--text);
    border-radius: 12px;
    padding: 12px 14px;
    min-height: 44px;

    display: inline-flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;

    font-size: 0.95rem;
    font-weight: 500;
    letter-spacing: .005em;
    cursor: pointer;

    transition: background 140ms ease, border-color 140ms ease, box-shadow 140ms ease;

    &:hover {
        background: #f4f7fb;
        border-color: #d6deea;
        box-shadow: 0 0 0 2px rgba(147, 197, 253, 0.25);
    }

    &:focus { outline: none; }
    &::-moz-focus-inner { border: 0; }
    &:focus-visible {
        box-shadow: 0 0 0 3px rgba(147, 197, 253, 0.5);
    }

    &[data-active="true"] {
        background: var(--bg-active);
        border-color: var(--border-active);
    }

    &[disabled] {
        opacity: .6;
        cursor: not-allowed;
    }
`;

const Left = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
`;

const IconWrap = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    flex: 0 0 18px;

    svg {
        color: ${p => p.color || "currentColor"};
    }
`;

const LabelText = styled.span`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const TickDot = styled.span`
    width: 22px;
    height: 22px;
    border-radius: 9999px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: background 160ms ease, border-color 160ms ease, color 160ms ease;
    flex: 0 0 22px;

    background: ${p => (p.active ? "#2563eb" : "transparent")};
    border: 2px solid ${p => (p.active ? "#2563eb" : "#cbd5e1")};
    color: ${p => (p.active ? "#ffffff" : "transparent")};

    ${Tag}:hover & {
        border-color: ${p => (p.active ? "#2563eb" : "#b6c2d0")};
    }
`;

const FilterRow = ({label, items, minItemWidth = 200}) => (
    <Row>
        <Grid container alignItems="center" spacing={2}>
            <Grid item xs={12} sm={3} md={2}>
                <Typography variant="body2">{label}</Typography>
            </Grid>
            <Grid item xs={12} sm={9} md={10}>
                <Box
                    display="grid"
                    style={{
                        gridTemplateColumns: `repeat(auto-fit, minmax(${minItemWidth}px, 1fr))`,
                        gridGap: 12
                    }}
                >
                    {items.map(item => {
                        const Icon = item.icon;
                        const active = !!item.checked;
                        const onClick = () => item.onChange && item.onChange({ target: { checked: !active } });

                        return (
                            <Tag
                                key={item.key || item.label}
                                onClick={onClick}
                                data-active={active}
                                aria-pressed={active}
                                disabled={item.disabled}
                                title={item.label}
                            >
                                <Left>
                                    {Icon && (
                                        <IconWrap color={item.color}>
                                            <Icon size={16}/>
                                        </IconWrap>
                                    )}
                                    <LabelText>{item.label}</LabelText>
                                </Left>
                                <TickDot active={active} aria-hidden="true">
                                    <FaCheck size={11}/>
                                </TickDot>
                            </Tag>
                        );
                    })}
                </Box>
            </Grid>
        </Grid>
    </Row>
);

export default FilterRow;
